import { Link } from "react-router";

import { BugIcon } from "lucide-react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { summaryPersonName } from "@/features/projects/helpers";
import { ProjectSummaryActivity } from "@/features/projects/types";
import { STATUS_CONFIG } from "@/features/tickets/config";
import { formatAbsoluteDateTime, formatRelativeDate } from "@/shared/lib/utils/date";

interface RecentActivityRowProps {
  activity: ProjectSummaryActivity;
  projectId: string;
  canReadTickets: boolean;
}

export function RecentActivityRow({ activity, projectId, canReadTickets }: RecentActivityRowProps) {
  const status = activity.status ? STATUS_CONFIG[activity.status] : null;
  const Icon = status?.icon ?? BugIcon;
  const tint = status?.className ?? "text-destructive";
  const name = summaryPersonName(activity.firstName, activity.lastName);
  const description = activity.status ? `${name} moved this to ${activity.status}` : `${name} opened this ticket`;
  const relativeDate = formatRelativeDate(activity.occurredAt);
  const absoluteDate = formatAbsoluteDateTime(activity.occurredAt);

  const content = (
    <div className="flex items-start gap-4 px-6 py-4">
      <Icon className={`mt-0.5 size-4 shrink-0 ${tint}`} />
      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <p className="truncate text-sm font-medium">
          <span className="font-mono text-xs text-muted-foreground">{activity.ticketKey}</span> - {activity.ticketTitle}
        </p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <span className="shrink-0 text-xs text-muted-foreground">{relativeDate}</span>
        </TooltipTrigger>
        <TooltipContent>{absoluteDate}</TooltipContent>
      </Tooltip>
    </div>
  );

  if (!canReadTickets) {
    return content;
  }

  return (
    <Link to={`/projects/${projectId}/tickets/${activity.ticketId}`} className="block transition-colors hover:bg-muted/50">
      {content}
    </Link>
  );
}
